import React, { useState, useEffect, useRef } from 'react'
import { Container, Card, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import StyledDropzone from '../StyledDropzone'
import EmailStep from './EmailStep'
import { generateRandomSubdomain } from '../../utils/general'
import { verifyOTP, processTiinyTransfer } from '../../services/transfer'
import { handlePresignedUploads } from '../../services/presigned-uploads/handlePresignedUploads'
import { getSiteContents } from '../../services/site'
import { setDraggedFile } from '../../Manage/actions'
import { showUpgradeCardModal, setShowPaymentModal } from '../../actions'
import styles from './TiinyTransfer.module.css'
import { DROPZONE_ACCEPTED_FILES_PRO, errors } from '../../constants'
import { pluralize, getTLDs } from 'utils/general'
import { promptForAdditionalFiles, removeFileFromUploadState, processAdditionalFiles } from 'utils/file-upload'
import SelectedFile from '../SelectedFile/SelectedFile'
import { isZipFile } from '../CreateUpdateSiteModal/interface'
import DropzoneWrapper from 'components/DragAndDrop/component/DropzoneWrapper'
import UpgradePromoCard from '../UpgradePromoModal'
import ProPlansModal from '../ProPlans/ProPlansModal'
import PlanPaymentModal from '../PlanPaymentModal/PlanPaymentModal'
import { LoadingBox } from '../CreateUpdateSiteModal/components/LoadingBox'
import UploadStats from '../UploadStats/UploadStats'
import { useUploadStats } from '../../hooks/useUploadStats'

const STEPS = {
  UPLOAD: 'UPLOAD',
  EMAIL: 'EMAIL',
  UPLOADING: 'UPLOADING',
  SUCCESS: 'SUCCESS'
}

const MIN_STATS_FILE_SIZE = 50 * 1024 * 1024 // 50MB

const TiinyTransfer = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const userProfile = useSelector((state) => state.manage.userProfile)
  const { showPaymentModal, upgradePlanId, periodProp } = useSelector((state) => state.upgrade)
  const { uploadStats, uploadProgress: updateUploadStats, resetStats } = useUploadStats()
  const draggedFile = useSelector((state) => state.manage.draggedFile)

  const [step, setStep] = useState(STEPS.UPLOAD)
  const [fileSelected, setFileSelected] = useState(null)
  const [files, setFiles] = useState([])
  const [error, setError] = useState('')
  const [transferSite, setTransferSite] = useState(null)
  const [isSuccessReady, setIsSuccessReady] = useState(false)
  const [apiResponse, setApiResponse] = useState(null)
  const [showStats, setShowStats] = useState(false)
  const [progress, setProgress] = useState(0)
  const [copied, setCopied] = useState(false)

  const lastDragTime = useRef(0)

  const handleFileSelect = (newFiles) => {
    if (newFiles && newFiles.length > 0) {
      dispatch(setDraggedFile(newFiles))
    }
  }

  useEffect(() => {
    let isMounted = true

    if (draggedFile) {
      const now = Date.now()
      if (now - lastDragTime.current < 500) {
        dispatch(setDraggedFile(null))
        return
      }
      lastDragTime.current = now

      const filesToAdd = Array.isArray(draggedFile) ? draggedFile : [draggedFile]

      const runProcess = async () => {
        const validFiles = await processAdditionalFiles({ filesToAdd, files, fileSelected })

        if (validFiles && validFiles.length > 0 && isMounted) {
          if (!fileSelected) {
            setFileSelected(validFiles[0])
            setFiles(validFiles.slice(1))
          } else {
            setFiles((prev) => [...prev, ...validFiles])
          }
        }

        if (isMounted) {
          dispatch(setDraggedFile(null))
        }
      }

      runProcess()
    }

    return () => {
      isMounted = false
    }
  }, [draggedFile, fileSelected, files, dispatch])

  useEffect(() => {
    if (apiResponse && isSuccessReady) {
      const rawLink = apiResponse.link
      const subdomain = typeof rawLink === 'string' ? rawLink : rawLink?.subdomain || apiResponse.subdomain || ''
      const isAlreadyActive = apiResponse.status === 'active'

      setTransferSite({
        subdomain,
        linkStatus: isAlreadyActive ? 'online' : 'processing'
      })
      setStep(STEPS.SUCCESS)
    }
  }, [apiResponse, isSuccessReady])

  useEffect(() => {
    if (step !== STEPS.SUCCESS || !transferSite?.subdomain) return
    const status = transferSite?.linkStatus?.toLowerCase()
    if (status === 'online' || status === 'error') return

    let cancelled = false
    const pollSiteStatus = async () => {
      try {
        const { data } = await getSiteContents({ domain: transferSite.subdomain, _t: Date.now() })
        if (cancelled) return
        const updatedStatus = data?.link?.linkStatus?.toLowerCase()
        if (updatedStatus === 'online' || updatedStatus === 'error') {
          setTransferSite((prev) => ({ ...prev, linkStatus: updatedStatus }))
        }
      } catch (e) {
        console.error(e)
      }
    }

    pollSiteStatus()
    const intervalId = setInterval(pollSiteStatus, 5000)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [step, transferSite?.subdomain, transferSite?.linkStatus])

  const handleAddMoreFiles = () => {
    promptForAdditionalFiles({
      files,
      fileSelected,
      onFilesValidated: (validFilesToAdd) => {
        setFiles([...files, ...validFilesToAdd])
      }
    })
  }

  const handleRemoveFile = (fileToRemove, isSelectedMainFile) => {
    removeFileFromUploadState({
      files,
      fileSelected,
      fileToRemove,
      isSelectedFile: isSelectedMainFile,
      onUpdateFiles: ({ newSelectedFile, newFiles }) => {
        setFileSelected(newSelectedFile)
        setFiles(newFiles)
      }
    })
  }

  const handleEmailSubmit = async (emailData) => {
    const { uploaderEmail, recipientEmails, otp, authenticated } = emailData
    setError('')
    setStep(STEPS.UPLOADING)
    resetStats()
    setProgress(0)
    setIsSuccessReady(false)
    setApiResponse(null)

    const allFiles = fileSelected ? [fileSelected, ...files] : files
    const totalSize = allFiles.reduce((acc, f) => acc + (f?.size || 0), 0)
    setShowStats(totalSize >= MIN_STATS_FILE_SIZE)

    try {
      if (!authenticated) {
        const verifyRes = await verifyOTP(uploaderEmail, otp)
        if (verifyRes?.token) {
          localStorage.setItem('token', verifyRes.token)
          localStorage.setItem('tt_email', uploaderEmail)
          localStorage.setItem('tt_verified', 'true')
        }
      }

      const tlds = getTLDs({ account: userProfile })
      const domainSuffix = tlds.length > 0 ? tlds[0] : '.tiiny.site'
      const domainToUpload = generateRandomSubdomain() + (domainSuffix ? domainSuffix : '.tiiny.site')
      const formData = new FormData()
      formData.append('email', uploaderEmail)
      formData.append('purpose', 'tiiny-transfer')
      formData.append('optionalEmails', recipientEmails.join(','))

      const response = await handlePresignedUploads({
        files: allFiles,
        domain: domainToUpload,
        newDomain: domainToUpload,
        formData,
        isUpdate: false,
        uploaderEmail,
        filesToAddPresignedTotalSize: totalSize,
        filesToAddPresignedMetadata: {
          totalFiles: allFiles.map((f) => ({ name: f.name, size: f.size, type: f.type })),
          completedFiles: []
        },
        uploadProgress: (event, sizeInfo, isPresigned, isFirstDone) => {
          updateUploadStats(event, sizeInfo || { size: totalSize }, isPresigned, isFirstDone)
          const currentProgress = Math.round(((100 * event.loaded) / (event.total || totalSize)) * 0.5)
          setProgress((prev) => (currentProgress > prev ? currentProgress : prev))
        },
        processFunction: processTiinyTransfer
      })

      if (response) {
        if (response.token) localStorage.setItem('token', response.token)
        localStorage.setItem('tt_verified', 'true')
        localStorage.setItem('tt_email', uploaderEmail)
      }

      setProgress(100)
      setApiResponse(response)
      setShowStats(false)
    } catch (err) {
      setShowStats(false)
      console.error(err)
      const errorData = err.response?.data
      const errorCode = errorData?.code || errorData?.error

      if (
        errorData?.showPaywall ||
        errorCode === errors.EXCEEDS_ACCOUNT_LIMIT ||
        errorCode === errors.EXCEEDS_SITE_LIMIT
      ) {
        const siteLimit = userProfile?.siteLimit ?? 1
        dispatch(
          showUpgradeCardModal({
            title: 'Upgrade to upload more content',
            desc: `Your current plan only allows ${siteLimit} live ${pluralize('project', 'projects', siteLimit)}. Upgrade to upload more.`,
            cta: 'Upgrade now',
            onClick: () => history.push('/login')
          })
        )
      } else {
        setError(errorData?.message || 'Upload failed. Please check your connection and try again.')
      }
      setStep(STEPS.EMAIL)
    }
  }

  const handleCopy = async () => {
    const link = transferSite?.subdomain ? `https://${transferSite.subdomain}` : ''
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  const handleReset = () => {
    setStep(STEPS.UPLOAD)
    setFileSelected(null)
    setFiles([])
    setError('')
    setTransferSite(null)
    setIsSuccessReady(false)
    setApiResponse(null)
    setProgress(0)
    setCopied(false)
  }

  const renderContent = () => {
    switch (step) {
      case STEPS.UPLOAD:
        return (
          <>
            <h2 className="text-center mb-4">Tiiny Transfer</h2>
            <p className="text-center text-muted mb-4">Upload files up to 100GB</p>
            {fileSelected ? (
              <div className="text-start">
                <div className={`mb-4 ${files.length > 0 ? 'file-list' : ''}`}>
                  <SelectedFile
                    file={fileSelected}
                    onRemove={() => handleRemoveFile(fileSelected, true)}
                    hideHomepageLabel={true}
                  />
                  {files.map((file) => (
                    <SelectedFile
                      key={file.name}
                      file={file}
                      onRemove={() => handleRemoveFile(file, false)}
                      hideHomepageLabel={true}
                    />
                  ))}
                </div>
                {!isZipFile(fileSelected?.type) && (
                  <Button
                    className="mb-4 mt-2 ms-auto d-block w-100"
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddMoreFiles}
                  >
                    + Add more files
                  </Button>
                )}
                <div className="d-flex justify-content-center">
                  <Button variant="primary" size="lg" className="w-100" onClick={() => setStep(STEPS.EMAIL)}>
                    Continue to Email Details
                  </Button>
                </div>
              </div>
            ) : (
              <StyledDropzone
                className={styles.dropzone}
                onFileSelect={handleFileSelect}
                acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}
                showExamples={false}
              />
            )}
          </>
        )

      case STEPS.EMAIL:
        return (
          <EmailStep
            files={fileSelected ? [fileSelected, ...files] : []}
            onNext={handleEmailSubmit}
            onBack={() => setStep(STEPS.UPLOAD)}
            error={error}
            setError={setError}
          />
        )

      case STEPS.UPLOADING:
        return (
          <div className="py-2">
            <LoadingBox
              progress={progress}
              message="Uploading your transfer..."
              userProfile={userProfile}
              dispatch={dispatch}
              onComplete={() => setIsSuccessReady(true)}
            />
            {showStats && (
              <UploadStats uploadStats={{ ...uploadStats, progress: Math.max(0.1, uploadStats.progress) }} />
            )}
            <p className="text-center text-muted mt-3">Please keep this tab open</p>
          </div>
        )

      case STEPS.SUCCESS: {
        const isLive = transferSite?.linkStatus?.toLowerCase() === 'online'
        const isError = transferSite?.linkStatus?.toLowerCase() === 'error'
        const isProcessing = !isLive && !isError
        const subdomain = transferSite?.subdomain || ''

        return (
          <div className="text-center py-4">
            <div
              className={`${styles.statusIcon} ${isLive ? styles.statusIconLive : isError ? styles.statusIconError : styles.statusIconProcessing}`}
            >
              {isLive ? (
                '✓'
              ) : isError ? (
                '✕'
              ) : (
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              )}
            </div>

            {isProcessing && (
              <>
                <h3 className="mb-2">Transfer Sent!</h3>
                <p className="text-muted mb-4">Your files are being processed. The link will be ready shortly.</p>
              </>
            )}
            {isLive && (
              <>
                <h3 className="mb-2">Transfer Complete!</h3>
                <p className="text-muted mb-4">Your files have been sent.</p>
              </>
            )}
            {isError && (
              <>
                <h3 className="mb-2">Transfer Failed</h3>
                <p className="text-muted mb-4">Something went wrong. Please try again.</p>
              </>
            )}

            {subdomain && (
              <div className="input-group mb-4">
                {isProcessing ? (
                  <input
                    type="text"
                    className="form-control text-muted fst-italic"
                    value={`https://${subdomain}`}
                    readOnly
                    disabled
                  />
                ) : (
                  <>
                    <input type="text" className="form-control" value={`https://${subdomain}`} readOnly />
                    <button className="btn btn-outline-primary" onClick={handleCopy}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </>
                )}
              </div>
            )}

            {isProcessing && (
              <p className="text-muted small mb-4">Please keep this tab open while processing completes.</p>
            )}

            {!isProcessing && (
              <button className="btn btn-primary btn-lg w-100" onClick={handleReset}>
                Send another
              </button>
            )}
          </div>
        )
      }

      default:
        return null
    }
  }

  return (
    <DropzoneWrapper acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}>
      <div className={styles.container}>
        <Container className="d-flex align-items-center justify-content-center min-vh-100 py-5">
          <Card className={`shadow-lg border-0 ${styles.card}`}>
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <img src="/assets/logo.png" alt="Tiiny Host" height="40" />
              </div>
              {renderContent()}
            </Card.Body>
          </Card>
        </Container>
        <UpgradePromoCard />
        <ProPlansModal email={userProfile?.email} />
        <PlanPaymentModal
          show={showPaymentModal}
          onHide={() => dispatch(setShowPaymentModal({ showPaymentModal: false, upgradePlanId: null }))}
          planId={upgradePlanId}
          periodProp={periodProp}
          history={history}
        />
      </div>
    </DropzoneWrapper>
  )
}

export default TiinyTransfer
